import {DEFAULT_DIAGNOSTIC_REGIONS, getRegionForCode} from './regions'
import {combineDiagnosticsTier, worstLatencyTier} from './thresholds'
import {DEFAULT_LATENCY_ITERATIONS, measureLatencyEndpoint} from './latency'
import {collectClientConfig} from './clientConfig'
import {runMediaDiagnostics} from './media'
import {
  TOOL_PORT_VERSION,
  getConnectInstanceLatencyEndpointUrl,
  getConnectInstanceUrlFromEnv,
  getDiagnosticsEnvironmentLabel,
  getRegionLatencyEndpointUrl,
  isConnectDiagnosticsEnabled,
  toConnectInstanceOrigin,
} from './urls'
import type {
  ClientConfigDiagnosticsResult,
  ConnectDiagnosticsReport,
  LatencyEndpointResult,
  MediaDiagnosticsResult,
  RunConnectDiagnosticsOptions,
} from './types'

const COOLDOWN_MS = 60_000

let inFlight: Promise<ConnectDiagnosticsReport | null> | null = null
let lastStartedAt = 0

const emptyClientConfig = (): ClientConfigDiagnosticsResult => ({
  onLine: typeof navigator === 'undefined' ? true : navigator.onLine,
  osName: 'Unknown',
  osVersion: '-',
  browserName: 'Unknown',
  userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
  isSupportedBrowser: false,
  rtcSupport: false,
  isMobileDevice: false,
  hasSpeakers: false,
  hasMicrophone: false,
  hasMicrophoneAccess: null,
  hasCamera: false,
  hasCameraAccess: null,
  audioInputDevices: [],
  audioOutputDevices: [],
  videoInputDevices: [],
  errorMessages: ['Client config skipped'],
})

const emptyMedia = (): MediaDiagnosticsResult => ({
  regions: [],
  success: true,
})

/**
 * Run full Connect diagnostics: client config → latency → media STUN.
 * Returns null when disabled, offline, or cooled down / already running.
 */
export const runConnectDiagnostics = async (
  options: RunConnectDiagnosticsOptions = {}
): Promise<ConnectDiagnosticsReport | null> => {
  if (!isConnectDiagnosticsEnabled()) return null
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return null

  const now = Date.now()
  if (inFlight) return inFlight
  if (lastStartedAt && now - lastStartedAt < COOLDOWN_MS) return null

  lastStartedAt = now
  inFlight = executeConnectDiagnostics(options).finally(() => {
    inFlight = null
  })
  return inFlight
}

/** @deprecated Prefer runConnectDiagnostics */
export const runLatencyDiagnostics = runConnectDiagnostics

const executeConnectDiagnostics = async (
  options: RunConnectDiagnosticsOptions
): Promise<ConnectDiagnosticsReport> => {
  const startedAt = Date.now()
  const connectInstanceUrl = toConnectInstanceOrigin(
    options.connectInstanceUrl || getConnectInstanceUrlFromEnv()
  )
  const regions = options.regions?.length
    ? options.regions
    : [...DEFAULT_DIAGNOSTIC_REGIONS]
  const iterations = options.iterations ?? DEFAULT_LATENCY_ITERATIONS

  // Phase order matches AWS tool (geolocation skipped on accept path).
  const clientConfig = options.skipClientConfig
    ? emptyClientConfig()
    : await collectClientConfig()

  const endpoints: LatencyEndpointResult[] = []
  if (connectInstanceUrl) {
    const instanceEndpoint = getConnectInstanceLatencyEndpointUrl(connectInstanceUrl)
    endpoints.push(
      await measureLatencyEndpoint(
        instanceEndpoint,
        {name: connectInstanceUrl, code: ''},
        iterations
      )
    )
  }

  for (const code of regions) {
    const region = getRegionForCode(code)
    if (!region) continue
    const regionUrl = getRegionLatencyEndpointUrl(region.latencyEndpoint)
    endpoints.push(
      await measureLatencyEndpoint(
        regionUrl,
        {name: `${region.name} (${region.code})`, code: region.code},
        iterations
      )
    )
  }

  const latencyTier = worstLatencyTier(endpoints.map((e) => e.tier))

  const mediaRegions = options.skipMedia ? [] : await runMediaDiagnostics(regions)
  const media: MediaDiagnosticsResult = {
    regions: mediaRegions,
    success: mediaRegions.length === 0 || mediaRegions.every((r) => r.success),
  }

  const endedAt = Date.now()
  const overallTier = combineDiagnosticsTier({
    latencyTier,
    media,
    clientConfig,
  })

  return {
    version: TOOL_PORT_VERSION,
    startedAt,
    endedAt,
    connectInstanceUrl,
    regions,
    environment: getDiagnosticsEnvironmentLabel(),
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    clientConfig,
    latency: {
      endpoints,
      overallTier: latencyTier,
    },
    media,
    overallTier,
  }
}

/** Test helper — clears cooldown / in-flight state. */
export const __resetLatencyDiagnosticsGuardsForTests = () => {
  inFlight = null
  lastStartedAt = 0
}

export const __resetConnectDiagnosticsGuardsForTests = __resetLatencyDiagnosticsGuardsForTests
